import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../config/supabase';

interface InvestorCertificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InvestorCertificationModal = ({
  isOpen,
  onClose,
}: InvestorCertificationModalProps) => {
  const [step, setStep] = useState(1);
  const [investorType, setInvestorType] = useState<'hnw' | 'sophisticated' | ''>('');
  const [hnwCriteria, setHnwCriteria] = useState<string[]>([]);
  const [sophisticatedCriteria, setSophisticatedCriteria] = useState<string[]>([]);
  const [showHnwDetails, setShowHnwDetails] = useState(false);
  const [showSophDetails, setShowSophDetails] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    phone: '',
    riskAcknowledgement: false,
    protectionLossAcknowledgement: false,
    signatureConfirmation: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleHnwCriteriaChange = (criteria: string) => {
    setHnwCriteria((prev) =>
      prev.includes(criteria) ? prev.filter((c) => c !== criteria) : [...prev, criteria]
    );
  };

  const handleSophisticatedCriteriaChange = (criteria: string) => {
    setSophisticatedCriteria((prev) =>
      prev.includes(criteria) ? prev.filter((c) => c !== criteria) : [...prev, criteria]
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const canProceedStep1 = investorType !== '';
  const canProceedStep2 =
    investorType === 'hnw' ? hnwCriteria.length > 0 : sophisticatedCriteria.length > 0;
  const canProceedStep3 =
    formData.fullName &&
    formData.email &&
    formData.addressLine1 &&
    formData.city &&
    formData.postcode &&
    formData.riskAcknowledgement &&
    formData.protectionLossAcknowledgement &&
    formData.signatureConfirmation;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const certificationDate = new Date().toISOString().split('T')[0];
    const validUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const fullAddress = `${formData.addressLine1}${formData.addressLine2 ? ', ' + formData.addressLine2 : ''}, ${formData.city}, ${formData.postcode}`;

    const investorTypeLabel = investorType === 'hnw' 
      ? 'High Net Worth Individual' 
      : 'Self-Certified Sophisticated Investor';
    const criteriaList = investorType === 'hnw' 
      ? hnwCriteria.join(', ') 
      : sophisticatedCriteria.join(', ');

    try {
      // First, save to database
      const { error: dbError } = await supabase
        .from('investor_certifications')
        .insert({
          investor_type: investorTypeLabel,
          certification_criteria: criteriaList,
          full_name: formData.fullName,
          email: formData.email,
          address: fullAddress,
          phone: formData.phone || null,
          risk_acknowledgement: formData.riskAcknowledgement,
          protection_loss_acknowledgement: formData.protectionLossAcknowledgement,
          signature_confirmation: formData.signatureConfirmation,
          certification_date: certificationDate,
          valid_until: validUntil,
          status: 'active',
        });

      if (dbError) {
        console.error('Database error:', dbError);
        alert('Failed to save certification. Please try again.');
        setIsSubmitting(false);
        return;
      }

      // Then, send certification email with PDF copy
      console.log('Sending certification email to:', formData.email);
      
      const emailResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-certification-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            fullName: formData.fullName,
            email: formData.email,
            address: fullAddress,
            phone: formData.phone || undefined,
            investorType: investorTypeLabel,
            certificationCriteria: criteriaList,
            certificationDate,
            validUntil,
          }),
        }
      );

      if (!emailResponse.ok) {
        const errorData = await emailResponse.json();
        console.error('Email sending failed:', errorData);
        alert(`Certification saved but email failed to send: ${errorData.error || 'Unknown error'}. Please contact enquiries@dulairpfo.com`);
        setIsSubmitting(false);
        return;
      }

      const emailResult = await emailResponse.json();
      console.log('Email sent successfully:', emailResult);

      // Only show success if both database and email succeeded
      setIsSuccess(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert(`An error occurred: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setInvestorType('');
    setHnwCriteria([]);
    setSophisticatedCriteria([]);
    setShowHnwDetails(false);
    setShowSophDetails(false);
    setFormData({
      fullName: '',
      email: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postcode: '',
      phone: '',
      riskAcknowledgement: false,
      protectionLossAcknowledgement: false,
      signatureConfirmation: false,
    });
    setIsSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-navy to-gray px-6 py-4 relative">
            <button
              onClick={handleClose}
              type="button"
              className="absolute top-3 right-4 w-9 h-9 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-gold/20 rounded-full">
                <i className="ri-shield-check-line text-gold text-xl"></i>
              </div>
              <div>
                <h2 className="text-white text-lg font-bold">Investor Self-Certification</h2>
                <p className="text-white/80 text-xs">
                  Financial Promotion Order 2005 (Articles 48 & 50A) - Valid for 12 months
                </p>
              </div>
            </div>

            {/* Progress Steps */}
            {!isSuccess && (
              <div className="flex items-center justify-center gap-2 mt-4">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        if (s < step) {
                          setStep(s);
                        }
                      }}
                      disabled={s > step}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                        step >= s ? 'bg-gold text-navy' : 'bg-white/20 text-white/60'
                      } ${s < step ? 'cursor-pointer hover:bg-gold/80' : s === step ? 'cursor-default' : 'cursor-not-allowed'}`}
                    >
                      {step > s ? <i className="ri-check-line text-base"></i> : s}
                    </button>
                    {s < 3 && (
                      <div
                        className={`w-12 h-0.5 mx-1.5 rounded-full transition-all ${
                          step > s ? 'bg-gold' : 'bg-white/20'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(95vh-140px)]">
            {isSuccess ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-8"
              >
                <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                  <motion.i
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', delay: 0.2 }}
                    className="ri-check-line text-4xl text-green-600"
                  ></motion.i>
                </div>
                <h3 className="text-xl font-bold text-navy mb-2">
                  Certification Submitted Successfully
                </h3>
                <p className="text-gray-600 text-sm mb-4 max-w-md mx-auto">
                  Thank you for completing your investor self-certification. Your statement is valid for
                  12 months from today and has been securely recorded.
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-w-sm mx-auto mb-5">
                  <p className="text-sm text-slate-700 mb-1">
                    <strong>Certification Date:</strong>{' '}
                    {new Date().toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                  <p className="text-sm text-slate-700">
                    <strong>Valid Until:</strong>{' '}
                    {new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 max-w-sm mx-auto mb-5">
                  <p className="text-xs text-green-800 flex items-center justify-center gap-2">
                    <i className="ri-mail-check-line text-green-600 text-base"></i>
                    A copy of your certification has been sent to <strong>{formData.email}</strong>
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  type="button"
                  className="px-6 py-2.5 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap text-sm font-semibold"
                >
                  Close
                </button>
              </motion.div>
            ) : (
              <form id="investor-certification-form" data-readdy-form onSubmit={handleSubmit}>
                {/* Step 1: Select Investor Type */}
                {step === 1 && (
                  <div>
                    <h3 className="text-navy font-bold text-lg mb-2">
                      Select Your Investor Category
                    </h3>

                    {/* Regulatory Context Box */}
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <i className="ri-information-line text-blue-600 text-base mt-0.5 flex-shrink-0"></i>
                        <p className="text-blue-900 text-xs leading-relaxed">
                          <strong>Legal Basis:</strong> This certification is required under the{' '}
                          <strong>Financial Services and Markets Act 2000 (Financial Promotion) Order 2005</strong>{' '}
                          (FPO 2005), specifically <strong>Articles 48 and 50A</strong>. As a private family office,
                          we are not FCA-authorised and therefore rely on these exemptions to communicate financial
                          promotions to qualifying individuals.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* High Net Worth Option */}
                      <div>
                        <div
                          className={`p-4 border-2 rounded-xl transition-all cursor-pointer ${
                            investorType === 'hnw'
                              ? 'border-gold bg-gold/5 shadow-md'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                          }`}
                          onClick={() => setInvestorType('hnw')}
                        >
                          <label className="flex items-start gap-3 cursor-pointer">
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                investorType === 'hnw' ? 'border-gold bg-gold' : 'border-gray-300'
                              }`}
                            >
                              {investorType === 'hnw' && (
                                <i className="ri-check-line text-white text-xs"></i>
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-navy text-sm mb-0.5">
                                High Net Worth Individual (Article 48, FPO 2005)
                              </h4>
                              <p className="text-navy/70 text-xs">
                                For individuals with annual income of £100,000+ or net assets of
                                £250,000+ (excluding primary residence and pension)
                              </p>
                            </div>
                          </label>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowHnwDetails(!showHnwDetails);
                          }}
                          className="mt-2 ml-8 flex items-center gap-1.5 text-xs text-navy/70 hover:text-navy transition-colors cursor-pointer font-medium"
                        >
                          <i className={`ri-arrow-${showHnwDetails ? 'up' : 'down'}-s-line text-sm`}></i>
                          {showHnwDetails ? 'Hide details' : 'Show details'}
                        </button>

                        {/* HNW Details */}
                        {showHnwDetails && (
                          <div className="mt-2 ml-8">
                            <div className="p-4 bg-cream border border-gold/30 rounded-lg space-y-3">
                              <p className="text-navy text-sm leading-relaxed font-semibold">
                                You qualify as a High Net Worth Individual under <strong>Article 48 of FPO 2005</strong> if,
                                in the <strong>financial year immediately preceding today</strong>, you had:
                              </p>
                              <div className="space-y-2.5">
                                <div className="flex items-start gap-2">
                                  <i className="ri-checkbox-circle-fill text-gold text-base mt-0.5 flex-shrink-0"></i>
                                  <div>
                                    <p className="font-bold text-navy text-sm">
                                      Annual income of £100,000 or more
                                    </p>
                                    <p className="text-black text-xs leading-relaxed">
                                      Includes salary, bonuses, dividends, and rental income.{' '}
                                      <strong>Excludes</strong> one-off pension withdrawals or lump sum payments.
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <i className="ri-checkbox-circle-fill text-gold text-base mt-0.5 flex-shrink-0"></i>
                                  <div>
                                    <p className="font-bold text-navy text-sm">
                                      Net assets of £250,000 or more
                                    </p>
                                    <p className="text-black text-xs leading-relaxed">
                                      Total assets minus liabilities. <strong>Excludes</strong> your
                                      primary residence, any loan secured on it, pension rights, and
                                      life insurance contracts.
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="p-2.5 bg-navy/5 border border-navy/20 rounded-md">
                                <p className="text-navy text-xs font-medium flex items-center gap-1.5">
                                  <i className="ri-information-line text-gold text-sm"></i>
                                  Note: You only need to meet ONE of these criteria to qualify.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Sophisticated Investor Option */}
                      <div>
                        <div
                          className={`p-4 border-2 rounded-xl transition-all cursor-pointer ${
                            investorType === 'sophisticated'
                              ? 'border-gold bg-gold/5 shadow-md'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                          }`}
                          onClick={() => setInvestorType('sophisticated')}
                        >
                          <label className="flex items-start gap-3 cursor-pointer">
                            <div
                              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                investorType === 'sophisticated'
                                  ? 'border-gold bg-gold'
                                  : 'border-gray-300'
                              }`}
                            >
                              {investorType === 'sophisticated' && (
                                <i className="ri-check-line text-white text-xs"></i>
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-bold text-navy text-sm mb-0.5">
                                Self-Certified Sophisticated Investor (Article 50A, FPO 2005)
                              </h4>
                              <p className="text-navy/70 text-xs">
                                For individuals with relevant investment experience, professional
                                background in private equity/finance, or company directorship
                              </p>
                            </div>
                          </label>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSophDetails(!showSophDetails);
                          }}
                          className="mt-2 ml-8 flex items-center gap-1.5 text-xs text-navy/70 hover:text-navy transition-colors cursor-pointer font-medium"
                        >
                          <i className={`ri-arrow-${showSophDetails ? 'up' : 'down'}-s-line text-sm`}></i>
                          {showSophDetails ? 'Hide details' : 'Show details'}
                        </button>

                        {/* Sophisticated Details */}
                        {showSophDetails && (
                          <div className="mt-2 ml-8">
                            <div className="p-4 bg-cream border border-gold/30 rounded-lg space-y-3">
                              <p className="text-navy text-sm leading-relaxed font-semibold">
                                You qualify as a Sophisticated Investor under <strong>Article 50A of FPO 2005</strong> if
                                you meet <strong>at least one</strong> of these criteria:
                              </p>
                              <div className="space-y-2.5">
                                <div className="flex items-start gap-2">
                                  <i className="ri-checkbox-circle-fill text-gold text-base mt-0.5 flex-shrink-0"></i>
                                  <div>
                                    <p className="font-bold text-navy text-sm">
                                      Made more than one investment in unlisted companies
                                    </p>
                                    <p className="text-black text-xs leading-relaxed">
                                      In the <strong>two years prior to today</strong>. Includes startups, private
                                      equity, EIS/SEIS schemes.
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <i className="ri-checkbox-circle-fill text-gold text-base mt-0.5 flex-shrink-0"></i>
                                  <div>
                                    <p className="font-bold text-navy text-sm">
                                      Member of a business angel network
                                    </p>
                                    <p className="text-black text-xs leading-relaxed">
                                      For at least <strong>6 months prior to today</strong> (e.g., UKBAA members).
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <i className="ri-checkbox-circle-fill text-gold text-base mt-0.5 flex-shrink-0"></i>
                                  <div>
                                    <p className="font-bold text-navy text-sm">Professional Background</p>
                                    <p className="text-black text-xs leading-relaxed">
                                      Work or have worked in the <strong>two years prior to today</strong> in a
                                      professional capacity in private equity or providing finance for SMEs.
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-2">
                                  <i className="ri-checkbox-circle-fill text-gold text-base mt-0.5 flex-shrink-0"></i>
                                  <div>
                                    <p className="font-bold text-navy text-sm">Company Director</p>
                                    <p className="text-black text-xs leading-relaxed">
                                      Current or past director (within <strong>2 years</strong>) of a
                                      company with an annual turnover of at least £1 million.
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="p-2.5 bg-navy/5 border border-navy/20 rounded-md">
                                <p className="text-navy text-xs font-medium flex items-center gap-1.5">
                                  <i className="ri-information-line text-gold text-sm"></i>
                                  Note: Meeting any ONE of these criteria qualifies you.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex justify-between">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:text-navy hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-arrow-left-line mr-1.5"></i>
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        disabled={!canProceedStep1}
                        className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                          canProceedStep1
                            ? 'bg-navy text-white hover:bg-navy/90 shadow-md hover:shadow-lg'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Continue to Certification
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Certification Criteria */}
                {step === 2 && (
                  <div>
                    {investorType === 'hnw' ? (
                      <>
                        <h3 className="text-navy font-bold text-lg mb-2">
                          High Net Worth Self-Certification (Article 48)
                        </h3>

                        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <i className="ri-error-warning-line text-amber-700 text-base mt-0.5 flex-shrink-0"></i>
                            <p className="text-amber-900 text-xs leading-relaxed">
                              <strong>Legal Declaration:</strong> By selecting the criteria below,
                              you are making a formal self-certification statement under{' '}
                              <strong>Article 48 of the Financial Promotion Order 2005</strong> that you met
                              these financial thresholds in the <strong>12 months immediately before today</strong>.
                            </p>
                          </div>
                        </div>

                        <p className="text-gray-700 text-sm mb-4 font-medium">
                          Please select <strong>at least one</strong> criterion that applies to you:
                        </p>

                        <div className="space-y-3">
                          <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-all">
                            <label className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={hnwCriteria.includes('income')}
                                onChange={() => handleHnwCriteriaChange('income')}
                                className="w-5 h-5 mt-0.5 accent-gold cursor-pointer flex-shrink-0"
                              />
                              <div className="flex-1">
                                <p className="text-navy text-sm font-bold mb-1">
                                  Annual Income of £100,000 or more
                                </p>
                                <p className="text-black text-xs leading-relaxed">
                                  I certify that my annual income was at least £100,000 in the financial year
                                  immediately preceding today's date.
                                </p>
                              </div>
                            </label>
                          </div>

                          <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-all">
                            <label className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={hnwCriteria.includes('assets')}
                                onChange={() => handleHnwCriteriaChange('assets')}
                                className="w-5 h-5 mt-0.5 accent-gold cursor-pointer flex-shrink-0"
                              />
                              <div className="flex-1">
                                <p className="text-navy text-sm font-bold mb-1">
                                  Net Assets of £250,000 or more
                                </p>
                                <p className="text-black text-xs leading-relaxed">
                                  I certify that my net assets were at least £250,000 throughout the financial year
                                  immediately preceding today's date (excluding primary residence, mortgage on it,
                                  pension rights, and life insurance).
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-navy font-bold text-lg mb-2">
                          Sophisticated Investor Self-Certification (Article 50A)
                        </h3>

                        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <i className="ri-error-warning-line text-amber-700 text-base mt-0.5 flex-shrink-0"></i>
                            <p className="text-amber-900 text-xs leading-relaxed">
                              <strong>Legal Declaration:</strong> By selecting the criteria below,
                              you are formally self-certifying under <strong>Article 50A of the Financial
                              Promotion Order 2005</strong> that you have sufficient experience and knowledge
                              to understand the risks of investing in unlisted companies.
                            </p>
                          </div>
                        </div>

                        <p className="text-gray-700 text-sm mb-4 font-medium">
                          Please select <strong>at least one</strong> criterion that applies to you:
                        </p>

                        <div className="space-y-3">
                          <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-all">
                            <label className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={sophisticatedCriteria.includes('investments')}
                                onChange={() => handleSophisticatedCriteriaChange('investments')}
                                className="w-5 h-5 mt-0.5 accent-gold cursor-pointer flex-shrink-0"
                              />
                              <div className="flex-1">
                                <p className="text-navy text-sm font-bold mb-1">
                                  Unlisted Company Investments
                                </p>
                                <p className="text-black text-xs leading-relaxed">
                                  I have made more than one investment in an unlisted company in the two years
                                  prior to today's date.
                                </p>
                              </div>
                            </label>
                          </div>

                          <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-all">
                            <label className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={sophisticatedCriteria.includes('angel')}
                                onChange={() => handleSophisticatedCriteriaChange('angel')}
                                className="w-5 h-5 mt-0.5 accent-gold cursor-pointer flex-shrink-0"
                              />
                              <div className="flex-1">
                                <p className="text-navy text-sm font-bold mb-1">
                                  Business Angel Network Member
                                </p>
                                <p className="text-black text-xs leading-relaxed">
                                  I am a member of a network or syndicate of business angels and have been so for
                                  at least six months prior to today's date.
                                </p>
                              </div>
                            </label>
                          </div>

                          <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-all">
                            <label className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={sophisticatedCriteria.includes('professional')}
                                onChange={() => handleSophisticatedCriteriaChange('professional')}
                                className="w-5 h-5 mt-0.5 accent-gold cursor-pointer flex-shrink-0"
                              />
                              <div className="flex-1">
                                <p className="text-navy text-sm font-bold mb-1">
                                  Private Equity / SME Finance Professional
                                </p>
                                <p className="text-black text-xs leading-relaxed">
                                  I work, or have worked in the two years prior to today, in a professional capacity
                                  in the private equity sector or in the provision of finance for small and medium
                                  enterprises.
                                </p>
                              </div>
                            </label>
                          </div>

                          <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-all">
                            <label className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={sophisticatedCriteria.includes('director')}
                                onChange={() => handleSophisticatedCriteriaChange('director')}
                                className="w-5 h-5 mt-0.5 accent-gold cursor-pointer flex-shrink-0"
                              />
                              <div className="flex-1">
                                <p className="text-navy text-sm font-bold mb-1">
                                  Company Director (£1M+ Turnover)
                                </p>
                                <p className="text-black text-xs leading-relaxed">
                                  I am currently, or have been in the two years prior to today, a director of a
                                  company with an annual turnover of at least £1 million.
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="mt-6 flex justify-between">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:text-navy hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-arrow-left-line mr-1.5"></i>
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => setStep(3)}
                        disabled={!canProceedStep2}
                        className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                          canProceedStep2
                            ? 'bg-navy text-white hover:bg-navy/90 shadow-md hover:shadow-lg'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Continue to Declaration
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Personal Details & Declaration */}
                {step === 3 && (
                  <div>
                    <h3 className="text-navy font-bold text-lg mb-2">
                      Personal Details &amp; Legal Declaration
                    </h3>

                    <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <p className="text-slate-700 text-xs leading-relaxed">
                        <strong>Data Collection Notice:</strong> Under the Financial Promotion Order 2005,
                        we are required to collect your full name and address as part of your signed
                        self-certification statement. This certification is valid for 12 months from today.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-navy text-xs font-bold mb-1.5">
                            Full Legal Name *
                          </label>
                          <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors text-black"
                            placeholder="Enter your full legal name"
                          />
                        </div>
                        <div>
                          <label className="block text-navy text-xs font-bold mb-1.5">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors text-black"
                            placeholder="your@email.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-navy text-xs font-bold mb-1.5">
                          Address Line 1 *
                        </label>
                        <input
                          type="text"
                          name="addressLine1"
                          value={formData.addressLine1}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors text-black"
                          placeholder="Street address"
                        />
                      </div>

                      <div>
                        <label className="block text-navy text-xs font-bold mb-1.5">
                          Address Line 2
                        </label>
                        <input
                          type="text"
                          name="addressLine2"
                          value={formData.addressLine2}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors text-black"
                          placeholder="Apartment, suite, etc. (optional)"
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-navy text-xs font-bold mb-1.5">
                            City *
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors text-black"
                            placeholder="City"
                          />
                        </div>
                        <div>
                          <label className="block text-navy text-xs font-bold mb-1.5">
                            Postcode *
                          </label>
                          <input
                            type="text"
                            name="postcode"
                            value={formData.postcode}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors text-black"
                            placeholder="Postcode"
                          />
                        </div>
                        <div>
                          <label className="block text-navy text-xs font-bold mb-1.5">
                            Phone (Optional)
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold transition-colors text-black"
                            placeholder="+44 XXX XXX XXXX"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Risk Warnings */}
                    <div className="mt-5 p-4 bg-red-50 border border-red-300 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 flex items-center justify-center bg-red-200 rounded-full flex-shrink-0">
                          <i className="ri-error-warning-line text-red-700 text-sm"></i>
                        </div>
                        <h4 className="text-red-900 font-bold text-sm">
                          Mandatory Risk Acknowledgements
                        </h4>
                      </div>

                      <p className="text-red-900 text-xs mb-3 leading-relaxed font-medium">
                        You must read and acknowledge all of the following risk warnings as required by
                        the Financial Promotion Order 2005:
                      </p>

                      <div className="space-y-2.5">
                        <div className="p-3 bg-white rounded-md border border-red-200">
                          <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                              type="checkbox"
                              name="riskAcknowledgement"
                              checked={formData.riskAcknowledgement}
                              onChange={handleInputChange}
                              required
                              className="w-5 h-5 mt-0.5 accent-red-600 cursor-pointer flex-shrink-0"
                            />
                            <div>
                              <p className="text-red-900 text-xs font-bold mb-1">
                                Investment Risk Warning *
                              </p>
                              <p className="text-black text-xs leading-relaxed">
                                I understand that investments in unlisted companies carry a high degree of risk.
                                I may lose all of my investment. These investments are illiquid.
                              </p>
                            </div>
                          </label>
                        </div>

                        <div className="p-3 bg-white rounded-md border border-red-200">
                          <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                              type="checkbox"
                              name="protectionLossAcknowledgement"
                              checked={formData.protectionLossAcknowledgement}
                              onChange={handleInputChange}
                              required
                              className="w-5 h-5 mt-0.5 accent-red-600 cursor-pointer flex-shrink-0"
                            />
                            <div>
                              <p className="text-red-900 text-xs font-bold mb-1">
                                Loss of Regulatory Protections *
                              </p>
                              <p className="text-black text-xs leading-relaxed mb-1.5">
                                I understand that by signing this statement, I will lose the protections normally
                                afforded to retail investors:
                              </p>
                              <ul className="text-black text-xs space-y-0.5 ml-3">
                                <li>• No access to Financial Ombudsman Service (FOS)</li>
                                <li>• No coverage by Financial Services Compensation Scheme (FSCS)</li>
                              </ul>
                            </div>
                          </label>
                        </div>

                        <div className="p-3 bg-white rounded-md border border-red-200">
                          <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                              type="checkbox"
                              name="signatureConfirmation"
                              checked={formData.signatureConfirmation}
                              onChange={handleInputChange}
                              required
                              className="w-5 h-5 mt-0.5 accent-red-600 cursor-pointer flex-shrink-0"
                            />
                            <div>
                              <p className="text-red-900 text-xs font-bold mb-1">
                                Declaration &amp; Signature Confirmation *
                              </p>
                              <p className="text-black text-xs leading-relaxed">
                                I confirm that this self-certification statement is valid for 12 months from today
                                ({new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}).
                                I am signing this declaration of my own free will. All information provided is true and accurate.
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Company Information */}
                    <div className="mt-4 p-4 bg-navy/5 rounded-lg border border-navy/20">
                      <p className="text-black text-xs font-bold mb-2">
                        This certification is being submitted to:
                      </p>
                      <div className="text-black text-xs leading-relaxed">
                        <p className="font-bold text-sm text-black">
                          Dulair Private Family Office
                        </p>
                        <p className="mt-1 text-black">Kings Head House, 15 London End, Beaconsfield, HP9 2HN</p>
                        <p className="mt-1 text-black/70">
                          Not FCA-authorised (FPO 2005 Articles 48 &amp; 50A)
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-between items-center">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:text-navy hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-arrow-left-line mr-1.5"></i>
                        Back
                      </button>
                      <button
                        type="submit"
                        disabled={!canProceedStep3 || isSubmitting}
                        className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                          canProceedStep3 && !isSubmitting
                            ? 'bg-gold text-navy hover:opacity-90 shadow-lg hover:shadow-xl'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-base"></i>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="ri-shield-check-line text-base"></i>
                            Submit Self-Certification
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InvestorCertificationModal;
